import { InformationCircleIcon } from '@heroicons/react/20/solid';
import * as React from 'react';

import type { TaskActivity } from '@/services/api/generated/taskActivityService';

import { TimelineEventRouter } from './TimelineEventRouter';
import { useTimeline } from './useTimeline';

interface RelatedActivityTimelineProps {
  matterId: string;
  taskId: string;
}

/**
 * Read-only timeline component for the Related Activity tab.
 * Shows domain events related to the task (coverage, providers, police reports, etc.)
 * but NOT task-specific events like status changes or assignments.
 */
export const RelatedActivityTimeline: React.FC<
  RelatedActivityTimelineProps
> = ({ matterId, taskId }) => {
  const { items, isLoading, error } = useTimeline({
    matterId,
    taskId,
    filter: 'related',
  });

  if (error) {
    return (
      <div className="text-xs font-medium text-red-600 py-3 px-2">
        Failed to load related activity
      </div>
    );
  }

  return (
    <div>
      {/* Timeline Stream */}
      <div className="divide-y divide-slate-200">
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-pulse text-slate-500 text-xs font-medium">
              Loading related activity...
            </div>
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="py-4 flex flex-col items-center justify-center text-gray-300">
            <div className="size-8 rounded-full bg-gray-50 flex items-center justify-center mb-1">
              <InformationCircleIcon className="size-4 text-gray-200" />
            </div>
            <p className="text-xs font-medium text-gray-400">
              No related activity yet
            </p>
          </div>
        )}
        {!isLoading &&
          items.length > 0 &&
          items.map((item) => {
            // Related activity only shows events (no updates or correlated groups)
            if (item.type !== 'event') return null;

            return (
              <TimelineEventRouter
                key={item.id}
                activityItem={item.activityItem as unknown as TaskActivity}
                matterId={matterId}
              />
            );
          })}
      </div>
    </div>
  );
};
